"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useAuth } from "@/contexts/AuthContext"
import { BookingService } from "@/lib/services/bookingService"
import { SlotService } from "@/lib/services/slotService"
import type { BookingWithDetails } from "@/lib/supabase"
import { Calendar, History, LogOut, Ban, Shield, AlertTriangle } from "lucide-react"
import UserGuard from "@/user/components/userGuard"
const UserDashboard = () => {
  const { user, logout, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const [recentBookings, setRecentBookings] = useState<BookingWithDetails[]>([])
  const [loadingBookings, setLoadingBookings] = useState(true)
  const [slotBlockReasons, setSlotBlockReasons] = useState<Record<string, string>>({})

  useEffect(() => {
    // Wait for auth loading to complete before checking authentication
    if (isLoading) return

    if (!isAuthenticated || user?.type !== "user") {
      navigate("/user-login")
      return
    }

    loadRecentBookings()
  }, [isAuthenticated, user, navigate, isLoading])

  const loadRecentBookings = async () => {
    if (!user || user.type !== "user") return

    try {
      const bookings = await BookingService.getUserBookings(user.id)
      setRecentBookings(bookings.slice(0, 3)) // Get latest 3 bookings

      // Load blocking reasons for rejected bookings
      const rejectedBookings = bookings.filter(b => b.status === 'rejected')
      const blockReasons: Record<string, string> = {}
      
      for (const booking of rejectedBookings) {
        const reason = await SlotService.getSlotBlockingReason(booking.slot_id)
        if (reason) {
          blockReasons[booking.slot_id] = reason
        }
      }
      
      setSlotBlockReasons(blockReasons)
    } catch (error) {
      console.error("Error loading recent bookings:", error)
    } finally {
      setLoadingBookings(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate("/user-login")
  }

  const formatTimeSlot = (timeStart: string, timeEnd: string) => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":")
      const hour = Number.parseInt(hours)
      const ampm = hour >= 12 ? "PM" : "AM"
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      return `${displayHour}:${minutes} ${ampm}`
    }

    return `${formatTime(timeStart)} - ${formatTime(timeEnd)}`
  }

  const getStatusDisplay = (booking: BookingWithDetails) => {
    const blockReason = slotBlockReasons[booking.slot_id]
    
    if (booking.status === "rejected" && blockReason) {
      if (blockReason === "Admin Booking") {
        return {
          label: "Cancelled (Admin)",
          className: "bg-orange-100 text-orange-800",
          icon: <Shield className="w-3 h-3" />
        }
      }
      return {
        label: "Cancelled (Blocked)",
        className: "bg-red-100 text-red-800",
        icon: <Ban className="w-3 h-3" />
      }
    }

    const statusMap = {
      approved: { label: "Confirmed", className: "bg-green-100 text-green-800", icon: null },
      pending: { label: "Pending", className: "bg-yellow-100 text-yellow-800", icon: null },
      rejected: { label: "Rejected", className: "bg-red-100 text-red-800", icon: null }
    }

    return statusMap[booking.status as keyof typeof statusMap] || {
      label: booking.status,
      className: "bg-gray-100 text-gray-800",
      icon: null
    }
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) return null

  const blockedBookings = recentBookings.filter(booking => 
    booking.status === "rejected" && slotBlockReasons[booking.slot_id]
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-blue-900">Gamesville Turf Portal</h1>
              <p className="text-gray-600">Welcome, {user.name || "User"}</p>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Show alert if there are blocked bookings */}
        {blockedBookings.length > 0 && (
          <Alert className="mb-6 border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>Important Notice:</strong> You have {blockedBookings.length} booking(s) that were cancelled by the admin. 
              {blockedBookings.some(b => slotBlockReasons[b.slot_id] === "Admin Booking") && 
                " Some slots were needed for official use."
              }
              <Button 
                variant="link" 
                className="ml-2 text-orange-700 underline p-0 h-auto"
                onClick={() => navigate("/booking-history")}
              >
                View details →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/booking")}>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Book New Slot
              </CardTitle>
              <CardDescription>Select date and time slots for your turf booking</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                • Book slots 1 day in advance
                <br />• Add up to 12 teammates
                <br />• <strong>One booking per date only</strong>
              </p>
              <Button className="w-full bg-blue-600 hover:bg-blue-700">Start Booking</Button>
            </CardContent>
          </Card>

          <Card
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate("/booking-history")}
          >
            <CardHeader>
              <CardTitle className="flex items-center">
                <History className="w-5 h-5 mr-2 text-green-600" />
                Booking History
              </CardTitle>
              <CardDescription>View and manage your past and upcoming bookings</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                • Check booking status
                <br />• Cancel future bookings
                <br />• View team details
                <br />• See cancellation reasons
              </p>
              <Button variant="outline" className="w-full">
                View History
              </Button>
            </CardContent>
          </Card>
        </div>

        {!loadingBookings && recentBookings.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Recent Bookings</CardTitle>
              <CardDescription>Your latest booking activities</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentBookings.map((booking) => {
                  const statusDisplay = getStatusDisplay(booking)
                  const blockReason = slotBlockReasons[booking.slot_id]
                  const isBlocked = booking.status === "rejected" && blockReason
                  
                  return (
                    <div 
                      key={booking.id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        isBlocked ? "bg-orange-50 border border-orange-200" : "bg-gray-50"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="font-medium">{booking.slot.date}</p>
                          {isBlocked && statusDisplay.icon}
                        </div>
                        <p className="text-sm text-gray-600">
                          {formatTimeSlot(booking.slot.time_start, booking.slot.time_end)}
                        </p>
                        {isBlocked && (
                          <p className="text-xs text-orange-700 mt-1">
                            Reason: {blockReason}
                            {booking.rejection_reason && (
                              <span className="block text-xs text-red-700 mt-1">
                                <strong>Blocking Reason:</strong> {booking.rejection_reason}
                              </span>
                            )}
                          </p>
                        )}
                        {booking.status === "rejected" && booking.rejection_reason && !isBlocked && (
                          <p className="text-xs text-red-700 mt-1">
                            <strong>Rejection Reason:</strong> {booking.rejection_reason}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${statusDisplay.className}`}
                        >
                          {statusDisplay.icon}
                          <span>{statusDisplay.label}</span>
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  )
}

export default UserDashboard